import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Unlink } from 'lucide-react';
import { Button } from '@/shared/components';
import type { FlatFamilyMember } from '@/shared/api';
import { useWholeFlatUnlink } from './useWholeFlatLink';

interface Props {
  roomId: string;
  roomName: string;
  wholeFlat: FlatFamilyMember;
  manageable: boolean;
}

/** Ein Zimmer: seine Gesamtwohnung und — mit bestätigter Folge — das Lösen. */
export function RoomMembership({ roomId, roomName, wholeFlat, manageable }: Props) {
  const [confirming, setConfirming] = useState(false);
  const { unlink, failure } = useWholeFlatUnlink(roomId, () => setConfirming(false));

  return (
    <div className="mt-4">
      <p className="text-ink text-sm">
        Zimmer der Gesamtwohnung{' '}
        <Link
          to="/properties/$id/stammdaten"
          params={{ id: wholeFlat.id }}
          className="focus-visible:ring-sage break-words rounded-sm font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2"
        >
          {wholeFlat.adminDisplayName}
        </Link>
        .
      </p>
      {manageable && !confirming && (
        <Button
          type="button"
          variant="link"
          className="mt-3 min-h-11 px-0 text-[13px]"
          leftIcon={<Unlink size={14} aria-hidden />}
          onClick={() => setConfirming(true)}
        >
          Verknüpfung lösen
        </Button>
      )}
      {manageable && confirming && (
        <div className="border-line bg-card-alt/40 mt-3 rounded-md border p-4">
          <p className="text-ink text-sm">
            „{roomName}“ und „{wholeFlat.adminDisplayName}“ sperren sich danach nicht mehr
            gegenseitig: Buchungen der Gesamtwohnung schließen dieses Zimmer nicht mehr, und
            umgekehrt. Die Verfügbarkeit beider wird neu abgeleitet.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="destructive"
              className="min-h-11"
              loading={unlink.isPending}
              onClick={() => unlink.mutate()}
            >
              Verknüpfung lösen
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="min-h-11"
              onClick={() => setConfirming(false)}
            >
              Abbrechen
            </Button>
          </div>
        </div>
      )}
      {failure && (
        <p role="alert" className="text-sealwax mt-3 text-sm">
          {failure}
        </p>
      )}
    </div>
  );
}
